const CREATE_DRAFT_MUTATION = `
  mutation SaveDraft(
    $id: ID!
    $fieldValues: [FormFieldValuesInput]!
    $saveAsDraft: Boolean!
    $entryMeta: SubmitFormMetaInput
  ) {
    submitGfForm(
      input: {
        id: $id
        fieldValues: $fieldValues
        saveAsDraft: $saveAsDraft
        entryMeta: $entryMeta
      }
    ) {
      resumeUrl
      errors {
        id
        message
      }
      entry {
        __typename
        ... on GfDraftEntry {
          id
          formDatabaseId
          resumeToken
        }
      }
    }
  }
`;

const UPDATE_DRAFT_MUTATION = `
  mutation UpdateDraft(
    $id: ID!
    $idType: DraftEntryIdTypeEnum!
    $fieldValues: [FormFieldValuesInput]
    $entryMeta: UpdateDraftEntryMetaInput
    $shouldValidate: Boolean
  ) {
    updateGfDraftEntry(
      input: {
        id: $id
        idType: $idType
        fieldValues: $fieldValues
        entryMeta: $entryMeta
        shouldValidate: $shouldValidate
      }
    ) {
      resumeUrl
      errors {
        id
        message
      }
      draftEntry {
        id
        formDatabaseId
        resumeToken
      }
    }
  }
`;

const DRAFT_ENTRY_QUERY = `
  query GetGfDraftEntry($id: ID!) {
    gfDraftEntry(id: $id, idType: RESUME_TOKEN) {
      id
      formDatabaseId
      resumeToken
      isDraft
      isSubmitted
      formFields(first: 600) {
        nodes {
          __typename
          databaseId
          type
          ... on TextField {
            value
            defaultValue
          }
          ... on TextAreaField {
            value
            defaultValue
          }
          ... on EmailField {
            value
            hasEmailConfirmation
          }
          ... on PhoneField {
            value
            defaultValue
          }
          ... on RadioField {
            value
            hasOtherChoice
            choices {
              value
            }
          }
          ... on SelectField {
            value
            defaultValue
          }
          ... on HiddenField {
            value
            defaultValue
          }
          ... on NumberField {
            value
            defaultValue
          }
          ... on WebsiteField {
            value
            defaultValue
          }
          ... on CheckboxField {
            checkboxValues {
              value
            }
          }
          ... on MultiSelectField {
            values
            value
          }
          ... on NameField {
            nameValues {
              prefix
              first
              middle
              last
              suffix
            }
          }
          ... on AddressField {
            addressValues {
              street
              lineTwo
              city
              state
              zip
              country
            }
          }
          ... on DateField {
            value
            defaultValue
            dateType
            dateFormat
          }
          ... on TimeField {
            value
            timeFormat
            timeValues {
              hours
              minutes
              amPm
            }
          }
          ... on ConsentField {
            value
            consentValue
          }
        }
      }
    }
  }
`;

const COUNTRY_NAME_TO_CODE = {
  Australia: 'AU',
  Canada: 'CA',
  'New Zealand': 'NZ',
  'United Kingdom': 'GB',
  'United States': 'US',
  'United States of America': 'US',
};

const COUNTRY_CODE_TO_NAME = {
  AU: 'Australia',
  CA: 'Canada',
  GB: 'United Kingdom',
  NZ: 'New Zealand',
  US: 'United States',
};

const US_STATE_CODE_TO_NAME = {
  AA: 'Armed Forces Americas',
  AE: 'Armed Forces Europe',
  AK: 'Alaska',
  AL: 'Alabama',
  AP: 'Armed Forces Pacific',
  AR: 'Arkansas',
  AS: 'American Samoa',
  AZ: 'Arizona',
  CA: 'California',
  CO: 'Colorado',
  CT: 'Connecticut',
  DC: 'District of Columbia',
  DE: 'Delaware',
  FL: 'Florida',
  GA: 'Georgia',
  GU: 'Guam',
  HI: 'Hawaii',
  IA: 'Iowa',
  ID: 'Idaho',
  IL: 'Illinois',
  IN: 'Indiana',
  KS: 'Kansas',
  KY: 'Kentucky',
  LA: 'Louisiana',
  MA: 'Massachusetts',
  MD: 'Maryland',
  ME: 'Maine',
  MI: 'Michigan',
  MN: 'Minnesota',
  MO: 'Missouri',
  MP: 'Northern Mariana Islands',
  MS: 'Mississippi',
  MT: 'Montana',
  NC: 'North Carolina',
  ND: 'North Dakota',
  NE: 'Nebraska',
  NH: 'New Hampshire',
  NJ: 'New Jersey',
  NM: 'New Mexico',
  NV: 'Nevada',
  NY: 'New York',
  OH: 'Ohio',
  OK: 'Oklahoma',
  OR: 'Oregon',
  PA: 'Pennsylvania',
  PR: 'Puerto Rico',
  RI: 'Rhode Island',
  SC: 'South Carolina',
  SD: 'South Dakota',
  TN: 'Tennessee',
  TX: 'Texas',
  UT: 'Utah',
  VA: 'Virginia',
  VI: 'U.S. Virgin Islands',
  VT: 'Vermont',
  WA: 'Washington',
  WI: 'Wisconsin',
  WV: 'West Virginia',
  WY: 'Wyoming',
};

const normalizeAddressStateFromDraft = ({ state, country }) => {
  if (!state || typeof state !== 'string') return state;

  const trimmedState = state.trim();
  if (!trimmedState) return state;
  if (trimmedState.length !== 2) return trimmedState;

  const countryUpper = `${country || ''}`.toUpperCase();
  if (countryUpper !== 'US') return trimmedState;

  return US_STATE_CODE_TO_NAME[trimmedState.toUpperCase()] || trimmedState;
};

const getDatePickerValue = (date) => {
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();

  return { day, month, year };
};

const normalizeDateType = (type) =>
  `${type || ''}`.trim().toLowerCase().replace(/[-_\s]/g, '');

const normalizeDateFormat = (format) =>
  `${format || ''}`.trim().toUpperCase();

const isDatePickerType = (type) =>
  ['PICKER', 'DATEPICKER'].includes(normalizeDateType(type).toUpperCase());

const formatDate = (date, type, format) => {
  if (!date) return null;

  const formattedDate =
    isDatePickerType(type)
      ? getDatePickerValue(date)
      : {
          day: date.day,
          month: date.month,
          year: date.year,
        };

  const { day, month, year } = formattedDate;

  switch (normalizeDateFormat(format)) {
    case 'MDY':
      return `${month}/${day}/${year}`;
    case 'DMY':
      return `${day}/${month}/${year}`;
    case 'DMY_DASH':
      return `${day}-${month}-${year}`;
    case 'DMY_DOT':
      return `${day}.${month}.${year}`;
    case 'YMD_SLASH':
      return `${year}/${month}/${day}`;
    case 'YMD_DASH':
      return `${year}-${month}-${day}`;
    case 'YMD_DOT':
      return `${year}.${month}.${day}`;
    default:
      return null;
  }
};

const padValue = (value) => {
  if (value === undefined || value === null) return undefined;
  const numeric = parseInt(value, 10);
  if (Number.isNaN(numeric)) return undefined;
  return `${numeric}`.padStart(2, '0');
};

const formatTime = (fieldResponse, timeFormat) => {
  if (typeof fieldResponse === 'string') {
    return fieldResponse;
  }

  const hour = padValue(fieldResponse?.hour);
  const minute = padValue(fieldResponse?.minute);

  if (!hour || !minute) {
    return null;
  }

  if (timeFormat === 'H24') {
    return `${hour}:${minute}`;
  }

  const ampm = (fieldResponse?.ampm || 'am').toLowerCase();
  return `${hour}:${minute} ${ampm}`;
};

const formatAddress = (fieldResponse, defaultCountry = 'United States') => {
  if (!fieldResponse || typeof fieldResponse !== 'object') {
    return fieldResponse;
  }

  const country = fieldResponse.country || defaultCountry;
  if (!country || typeof country !== 'string') {
    return fieldResponse;
  }

  const normalizedCountry = country.trim();
  if (normalizedCountry.length === 2) {
    return {
      ...fieldResponse,
      country: normalizedCountry.toUpperCase(),
    };
  }

  return {
    ...fieldResponse,
    country: COUNTRY_NAME_TO_CODE[normalizedCountry] || normalizedCountry,
  };
};

const formatFieldValue = ({ id, fieldResponse, serverDataItem, clientData }) => {
  const { type, inputs, choices, timeFormat } = serverDataItem;

  switch (type) {
    case 'ADDRESS':
      return {
        addressValues: formatAddress(
          fieldResponse,
          serverDataItem.defaultCountry || 'United States'
        ),
      };
    case 'CAPTCHA':
      return {
        value: fieldResponse,
      };
    case 'CHECKBOX': {
      const selectedChoices = [];
      const selectedValues = Array.isArray(fieldResponse)
        ? fieldResponse
        : [fieldResponse].filter(Boolean);

      (choices || []).forEach(({ value }, index) => {
        const isSelected = selectedValues.includes(value);
        if (isSelected) {
          selectedChoices.push({ inputId: inputs?.[index]?.id, value });
        }
      });

      return {
        checkboxValues: selectedChoices,
      };
    }
    case 'EMAIL':
      if (inputs?.length > 0) {
        return {
          emailValues: {
            value: fieldResponse,
            confirmationValue: clientData[`input_${id}_2`],
          },
        };
      }

      return {
        emailValues: {
          value: fieldResponse,
        },
      };
    case 'FILEUPLOAD':
      return {
        fileUploadValues: fieldResponse,
      };
    case 'NAME':
      return { nameValues: fieldResponse };
    case 'HIDDEN':
    case 'HONEYPOT':
    case 'PASSWORD':
    case 'PHONE':
    case 'POSTCONTENT':
    case 'POSTEXCERPT':
    case 'POSTTITLE':
    case 'SELECT':
    case 'SIGNATURE':
    case 'TEXT':
    case 'TEXTAREA':
    case 'WEBSITE':
      return {
        value: fieldResponse,
      };
    case 'NUMBER':
      return {
        value: fieldResponse,
      };
    case 'RADIO': {
      const value =
        fieldResponse === 'gf_other_choice'
          ? clientData[`input_${id}_other`]
          : fieldResponse;
      return {
        value,
      };
    }
    case 'TIME':
      return {
        value: formatTime(fieldResponse, timeFormat),
      };
    case 'DATE':
      return {
        value: formatDate(
          fieldResponse,
          serverDataItem.dateType,
          serverDataItem.dateFormat
        ),
      };
    case 'MULTISELECT':
    case 'POSTCATEGORY':
    case 'POSTCUSTOM':
    case 'POSTTAGS':
      return {
        values: fieldResponse,
      };
    case 'CONSENT':
      return {
        value: fieldResponse ? 'value' : undefined,
      };
    default:
      return {};
  }
};

export const formatGravityFormPayload = ({ serverData, clientData }) => {
  if (!Array.isArray(serverData)) return [];

  return serverData
    .map(({ id, ...rest }) => {
      const fieldResponse = clientData?.[`input_${id}`];
      if (!fieldResponse) return null;

      return {
        id,
        ...formatFieldValue({ id, fieldResponse, clientData, serverDataItem: rest }),
      };
    })
    .filter(Boolean);
};

const isFileList = (value) =>
  typeof FileList !== 'undefined' && value instanceof FileList;

const isFileArray = (value) =>
  typeof File !== 'undefined' &&
  Array.isArray(value) &&
  value.length > 0 &&
  value[0] instanceof File;

const hasFileUploadFields = (fieldValues) =>
  fieldValues.some((field) =>
    Object.prototype.hasOwnProperty.call(field, 'fileUploadValues')
  );

const getDefaultGraphqlUrl = () => {
  if (process.env.NEXT_PUBLIC_WORDPRESS_FORM_SUBMIT_URL) {
    return process.env.NEXT_PUBLIC_WORDPRESS_FORM_SUBMIT_URL;
  }
  if (process.env.NEXT_PUBLIC_WORDPRESS_API_URL) {
    return process.env.NEXT_PUBLIC_WORDPRESS_API_URL;
  }
  if (process.env.NEXT_PUBLIC_WORDPRESS_URL) {
    return `${process.env.NEXT_PUBLIC_WORDPRESS_URL.replace(/\/$/, '')}/graphql`;
  }
  return '';
};

const executeGraphql = async (body, { baseUrl, isMultipart = false } = {}) => {
  const endpoint = baseUrl || getDefaultGraphqlUrl();
  if (!endpoint) {
    throw new Error('Missing GraphQL endpoint for save-and-continue.');
  }

  const res = await fetch(endpoint, {
    method: 'POST',
    headers: isMultipart ? {} : { 'Content-Type': 'application/json' },
    body: isMultipart ? body : JSON.stringify(body),
  });

  let json;
  try {
    json = await res.json();
  } catch (error) {
    throw new Error(`Failed to parse GraphQL response (${res.status}).`);
  }

  if (!res.ok) {
    const message = json?.errors?.[0]?.message || `GraphQL request failed (${res.status}).`;
    throw new Error(message);
  }

  return json;
};

const buildMultipartBody = ({ mutationQuery, fieldValues, variables }) => {
  const operations = {
    query: mutationQuery,
    variables: {
      ...variables,
      fieldValues: fieldValues.map((field) =>
        Object.prototype.hasOwnProperty.call(field, 'fileUploadValues')
          ? {
              ...field,
              fileUploadValues: null,
            }
          : field
      ),
    },
  };

  const formData = new FormData();
  formData.append('operations', JSON.stringify(operations));

  const fileMap = {};

  fieldValues.forEach((field, fieldIndex) => {
    const fileValues = field?.fileUploadValues;
    if (!isFileList(fileValues) && !isFileArray(fileValues)) {
      return;
    }

    Array.from(fileValues).forEach((file, fileIndex) => {
      const fileKey = `file_${fieldIndex}_${fileIndex}`;
      formData.append(fileKey, file);
      fileMap[fileKey] = [
        `variables.fieldValues.${fieldIndex}.fileUploadValues.${fileIndex}`,
      ];
    });
  });

  formData.append('map', JSON.stringify(fileMap));

  return formData;
};

const getDraftPayload = (responseJson) =>
  responseJson?.data?.updateGfDraftEntry || responseJson?.data?.submitGfForm || null;

const normalizeErrors = (responseJson, payloadErrors = []) => {
  const graphqlErrors = responseJson?.errors || [];

  return [
    ...payloadErrors.map((error) => ({
      id: error?.id,
      message: error?.message || 'An unknown field error occurred.',
    })),
    ...graphqlErrors.map((error) => ({
      id: 'graphql',
      message: error?.message || 'A GraphQL error occurred.',
    })),
  ];
};

const isPresent = (value) => {
  if (value === undefined || value === null) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'object') return Object.keys(value).length > 0;
  return true;
};

const getDateParts = (value, format) => {
  if (!value || typeof value !== 'string') return null;

  const parts = value.split(/[/.-]/).map((part) => part.trim());
  if (parts.length !== 3) return null;

  let day;
  let month;
  let year;

  switch (normalizeDateFormat(format)) {
    case 'MDY':
      [month, day, year] = parts;
      break;
    case 'DMY':
    case 'DMY_DASH':
    case 'DMY_DOT':
      [day, month, year] = parts;
      break;
    case 'YMD_SLASH':
    case 'YMD_DASH':
    case 'YMD_DOT':
      [year, month, day] = parts;
      break;
    default:
      return null;
  }

  if (!day || !month || !year) return null;

  const dayInt = Number.parseInt(day, 10);
  const monthInt = Number.parseInt(month, 10);
  const yearInt = Number.parseInt(year, 10);

  if (
    Number.isNaN(dayInt) ||
    Number.isNaN(monthInt) ||
    Number.isNaN(yearInt) ||
    dayInt < 1 ||
    dayInt > 31 ||
    monthInt < 1 ||
    monthInt > 12
  ) {
    return null;
  }

  return {
    day: day.padStart(2, '0'),
    month: month.padStart(2, '0'),
    year,
  };
};

const getDateValueFromDraft = ({ value, dateType, dateFormat }) => {
  const dateValue = value || '';
  if (!dateValue) return undefined;

  const dateParts = getDateParts(dateValue, dateFormat);

  if (isDatePickerType(dateType)) {
    if (dateParts) {
      const year = Number.parseInt(dateParts.year, 10);
      const month = Number.parseInt(dateParts.month, 10);
      const day = Number.parseInt(dateParts.day, 10);
      const parsedDate = new Date(year, month - 1, day);

      if (
        parsedDate.getFullYear() === year &&
        parsedDate.getMonth() === month - 1 &&
        parsedDate.getDate() === day
      ) {
        return parsedDate;
      }
    }

    const fallbackDate = new Date(dateValue);
    return Number.isNaN(fallbackDate.getTime()) ? undefined : fallbackDate;
  }

  return dateParts || undefined;
};

const getTimeValueFromDraft = ({ value, timeValues, timeFormat }) => {
  if (isPresent(timeValues?.hours) || isPresent(timeValues?.minutes)) {
    const baseValue = {
      hour: `${timeValues?.hours || ''}`,
      minute: `${timeValues?.minutes || ''}`,
    };

    if (timeFormat !== 'H24') {
      baseValue.ampm = `${timeValues?.amPm || 'am'}`.toLowerCase();
    }

    return baseValue;
  }

  if (typeof value !== 'string' || !value.trim()) {
    return undefined;
  }

  const raw = value.trim().toLowerCase();
  const match = raw.match(/(\d{1,2}):(\d{2})(?:\s*(am|pm))?/);

  if (!match) return undefined;

  const [, hour, minute, ampm] = match;
  const parsedValue = { hour, minute };

  if (timeFormat !== 'H24') {
    parsedValue.ampm = ampm || 'am';
  }

  return parsedValue;
};

const getAddressValueFromDraft = (addressValues) => {
  if (!addressValues || typeof addressValues !== 'object') return undefined;

  const countryRaw = addressValues.country
    ? `${addressValues.country}`.toUpperCase()
    : '';

  return {
    street: addressValues.street || '',
    lineTwo: addressValues.lineTwo || '',
    city: addressValues.city || '',
    state: normalizeAddressStateFromDraft({
      state: addressValues.state || '',
      country: countryRaw,
    }),
    zip: addressValues.zip || '',
    country: COUNTRY_CODE_TO_NAME[countryRaw] || addressValues.country || '',
  };
};

const getCheckboxValueFromDraft = (checkboxValues) => {
  if (!Array.isArray(checkboxValues)) return undefined;

  const values = checkboxValues
    .map((item) => item?.value)
    .filter((value) => isPresent(value));

  return values.length ? values : undefined;
};

const getMultiselectValueFromDraft = ({ values, value }) => {
  if (Array.isArray(values) && values.length) {
    return values.filter((item) => isPresent(item));
  }

  if (typeof value === 'string' && value.trim()) {
    return value.split(',').map((item) => item.trim()).filter(Boolean);
  }

  return undefined;
};

const buildDraftFieldValues = (draftEntry) => {
  const nodes = draftEntry?.formFields?.nodes;
  if (!Array.isArray(nodes)) return {};

  const values = {};

  nodes.forEach((field) => {
    const fieldId = field?.databaseId;
    if (!fieldId) return;

    const key = `input_${fieldId}`;
    let mappedValue;

    switch (field.__typename) {
      case 'CheckboxField':
        mappedValue = getCheckboxValueFromDraft(field.checkboxValues);
        break;
      case 'MultiSelectField':
        mappedValue = getMultiselectValueFromDraft(field);
        break;
      case 'NameField':
        mappedValue = field.nameValues || undefined;
        if (mappedValue && typeof mappedValue === 'object') {
          Object.entries(mappedValue).forEach(([partKey, partValue]) => {
            if (!isPresent(partValue)) return;
            if (!Object.prototype.hasOwnProperty.call(values, partKey)) {
              values[partKey] = partValue;
            }
          });
        }
        break;
      case 'AddressField':
        mappedValue = getAddressValueFromDraft(field.addressValues);
        if (mappedValue && typeof mappedValue === 'object') {
          Object.entries(mappedValue).forEach(([partKey, partValue]) => {
            if (!isPresent(partValue)) return;
            if (!Object.prototype.hasOwnProperty.call(values, partKey)) {
              values[partKey] = partValue;
            }
          });
        }
        break;
      case 'DateField':
        mappedValue = getDateValueFromDraft({
          value: field.value || field.defaultValue,
          dateType: field.dateType,
          dateFormat: field.dateFormat,
        });
        break;
      case 'TimeField':
        mappedValue = getTimeValueFromDraft({
          value: field.value,
          timeValues: field.timeValues,
          timeFormat: field.timeFormat,
        });
        break;
      case 'ConsentField':
        mappedValue = field.consentValue || isPresent(field.value) ? true : undefined;
        break;
      case 'RadioField': {
        const draftValue = field.value || undefined;
        if (!isPresent(draftValue)) {
          mappedValue = undefined;
          break;
        }

        if (field.hasOtherChoice) {
          const knownChoices = (field.choices || [])
            .map((choice) => choice?.value)
            .filter((choiceValue) => isPresent(choiceValue));
          const isKnownChoice = knownChoices.includes(draftValue);

          if (!isKnownChoice) {
            mappedValue = 'gf_other_choice';
            values[`${key}_other`] = draftValue;
            break;
          }
        }

        mappedValue = draftValue;
        break;
      }
      case 'EmailField':
        mappedValue = field.value || undefined;
        if (field.hasEmailConfirmation && isPresent(mappedValue)) {
          values[`${key}_2`] = mappedValue;
        }
        break;
      case 'FileUploadField':
        // Browser security constraints prevent setting file input values programmatically.
        // Keep uploads empty on restore so users can re-upload intentionally.
        mappedValue = undefined;
        break;
      default:
        mappedValue = field.value || field.defaultValue || undefined;
        break;
    }

    if (isPresent(mappedValue)) {
      values[key] = mappedValue;
    }
  });

  return values;
};

export const saveGravityFormDraft = async ({
  formId,
  fieldValues,
  baseUrl,
  entryMeta = {},
  resumeToken,
}) => {
  const cleanedEntryMeta = Object.fromEntries(
    Object.entries(entryMeta).filter(([, value]) => value !== undefined && value !== null && value !== '')
  );

  const normalizedResumeToken =
    typeof resumeToken === 'string' ? resumeToken.trim() : '';
  const isUpdatingExistingDraft = Boolean(normalizedResumeToken);

  const variables = isUpdatingExistingDraft
    ? {
        id: normalizedResumeToken,
        idType: 'RESUME_TOKEN',
        fieldValues,
        entryMeta: Object.keys(cleanedEntryMeta).length ? cleanedEntryMeta : undefined,
        shouldValidate: false,
      }
    : {
        id: formId,
        fieldValues,
        saveAsDraft: true,
        entryMeta: Object.keys(cleanedEntryMeta).length ? cleanedEntryMeta : undefined,
      };

  const mutationQuery = isUpdatingExistingDraft
    ? UPDATE_DRAFT_MUTATION
    : CREATE_DRAFT_MUTATION;

  const responseJson = hasFileUploadFields(fieldValues)
    ? await executeGraphql(
        buildMultipartBody({ mutationQuery, fieldValues, variables }),
        { baseUrl, isMultipart: true }
      )
    : await executeGraphql(
        {
          query: mutationQuery,
          variables,
        },
        { baseUrl }
      );

  const payload = getDraftPayload(responseJson);
  const entry = payload?.draftEntry || payload?.entry || null;
  const payloadErrors = payload?.errors || [];
  const resumeUrl = payload?.resumeUrl || '';
  const resumeTokenFromEntry = entry?.resumeToken || '';
  const resumeTokenFromUrl =
    resumeUrl && typeof URL !== 'undefined'
      ? (() => {
          try {
            const parsedUrl = new URL(resumeUrl, 'http://localhost');
            return parsedUrl.searchParams.get('gf_token') || '';
          } catch (error) {
            return '';
          }
        })()
      : '';
  const resolvedResumeToken =
    resumeTokenFromEntry || resumeTokenFromUrl || normalizedResumeToken;

  return {
    resumeUrl,
    resumeToken: resolvedResumeToken,
    errors: normalizeErrors(responseJson, payloadErrors),
    entry,
    raw: responseJson,
  };
};

export const fetchGravityFormDraft = async ({ resumeToken, baseUrl }) => {
  if (!resumeToken) {
    throw new Error('Missing resume token.');
  }

  const responseJson = await executeGraphql(
    {
      query: DRAFT_ENTRY_QUERY,
      variables: {
        id: resumeToken,
      },
    },
    { baseUrl }
  );

  const graphqlErrors = (responseJson?.errors || []).map((error) => ({
    id: 'graphql',
    message: error?.message || 'A GraphQL error occurred.',
  }));

  return {
    draftEntry: responseJson?.data?.gfDraftEntry || null,
    fieldValues: buildDraftFieldValues(responseJson?.data?.gfDraftEntry || null),
    errors: graphqlErrors,
    raw: responseJson,
  };
};
